import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useState } from "react";
import { Table, Tbody, Tcol, Thead, Trow } from "../../Generic/Table";
import EmptyState from "../../Generic/EmptyState";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as TaskActions from "../actions/taskActions";
import { TASK_STATUSES } from "../TaskConstants";
import { beautifyKey } from "../../util/util";
import Loader from "../../Generic/Loader";
import useWindowSize from "../../util/hooks/useWindowSize";
import usePolling from "../../util/hooks/usePolling";
import { zIndex } from "../../config/zIndex";
import { LIST_DISPLAY_DATE_FORMAT } from "../../util/date.util";
import Timestamp from "../../Generic/Timestamp";
import {
	POLL_INTERVAL_MS,
	TASK_LIST_TOP_SENTINEL_ID,
	scrollToRequiredItem,
} from "./taskList.helpers";
import {
	applyPendingNewer,
	fetchFirstPage,
	fetchNewer,
	fetchOlder,
	resetTaskListFetchLocks,
} from "./taskList.fetch";

const Page = styled.div`
	display: flex;
	height: max-content;
	flex-direction: column;
	text-align: left;
	padding: 2rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
	anchor-name: --page; 
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	margin: 0 0 1rem 0.5rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

const NewTasksChipWrap = styled.div`
	position: fixed;
	/* Bind the child's coordinates to the center of the anchor */
	position-anchor: --page;
	top: 10rem;
	z-index: ${zIndex.aboveHeader};
	justify-self: anchor-center;
`;

const NewTasksChip = styled.button`
	border: 0;
	cursor: pointer;
	background: rgba(45, 35, 66, 1);
	color: #ffffff;
	font-size: 0.85rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	padding: 0.5rem 1rem;
	border-radius: 20px;
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
	min-height: 2.25rem;
	&:disabled {
		cursor: wait;
		opacity: 1;
	}
`;

const ChipSpinner = styled.span`
	width: 0.85rem;
	height: 0.85rem;
	border-radius: 50%;
	border: 2px solid rgba(255, 255, 255, 0.35);
	border-top-color: #ffffff;
	animation: task-chip-spin 0.6s linear infinite;
	flex-shrink: 0;
	@keyframes task-chip-spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

const TaskListContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0.5rem;
`;

const TaskCard = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem 1rem;
	align-items: start;
	background: #fff;
	border-radius: 10px;
	padding: 1rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	cursor: pointer;
	overflow: hidden;
	transition: box-shadow 0.15s ease;
	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
	& > div:nth-child(1) {
		grid-column: 1;
		grid-row: 1;
	}
	& > div:nth-child(2) {
		grid-column: 1;
		grid-row: 3;
	}
	& > div:nth-child(3) {
		grid-column: 1;
		grid-row: 2;
	}
	& > div:nth-child(4) {
		grid-column: 2;
		grid-row: 1;
	}
	& > div:nth-child(5) {
		grid-column: 2;
		grid-row: 2;
	}
	& > div:nth-child(6) {
		grid-column: 2;
		grid-row: 3;
	}
`;

const Cell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	max-width: 100%;
`;

const CellLabel = styled.span`
	display: block;
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
	margin-bottom: 0.2rem;
`;

const CellValue = styled.span`
	font-size: 0.9rem;
	font-weight: 500;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	${(props) =>
		props.noEllipsis &&
		`
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
		overflow-wrap: anywhere;
	`}
`;

const StatusBadge = styled.div`
	align-self: flex-start;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	padding: 0.35rem 0.8rem;
	border-radius: 20px;
	white-space: nowrap;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const TASK_STATUS_META = {
	"1": { backColor: "#f6ea76", textColor: "#776200" },
	"2": { backColor: "#54BF29", textColor: "#ffffff" },
	"3": { backColor: "#008000", textColor: "#ffffff" },
	"4": { backColor: "#d64a4a", textColor: "#ffffff" },
	"5": { backColor: "#d64a4a", textColor: "#ffffff" },
	"6": { backColor: "#d64a4a", textColor: "#ffffff" },
	"10": { backColor: "#d64a4a", textColor: "#ffffff" },
	"11": { backColor: "#3b82f6", textColor: "#ffffff" },
	"12": { backColor: "#6b7280", textColor: "#ffffff" },
};

const getTaskStatusMeta = (status) => ({
	label: TASK_STATUSES[status] || beautifyKey(status),
	...(TASK_STATUS_META[status] || { backColor: "#e5e7eb", textColor: "#374151" }),
});

const TaskList = (props) => {
	const { machineID, kitchenId, taskList, startCursor, endCursor, hasMoreOlder, setTaskList, lastClickedElement, clearState, setLastClickedElement } = props;
	const navigate = useNavigate();
	const { state } = useLocation();
	const preserveState = state?.preserveState && taskList?.length ? true : false;
	const [loading, setLoading] = useState(!preserveState);
	const [loadingOlder, setLoadingOlder] = useState(false);
	const [liveEnabled, setLiveEnabled] = useState(false);
	const [pendingNewer, setPendingNewer] = useState([]);
	const [pendingIsGap, setPendingIsGap] = useState(false);
	const [pendingStartCursor, setPendingStartCursor] = useState(null);
	const [chipBusy, setChipBusy] = useState(false);
	const [lastElement, setLastElement] = useState();
	const [width] = useWindowSize();
	const isMobile = width < BREAKPOINTS.tablet;
	const visibleTasks = !preserveState && loading ? [] : taskList;

	useEffect(() => {
		return () => {
			resetTaskListFetchLocks();
		};
	}, []);

	useEffect(() => {
		if (preserveState && lastClickedElement?.id) {
			requestAnimationFrame(() => {
				scrollToRequiredItem(lastClickedElement.id);
				setLiveEnabled(true);
			});
			return;
		}
		if (preserveState) {
			setLiveEnabled(true);
			return;
		}
		clearState();
	}, [preserveState]);

	useEffect(() => {
		if (preserveState) return;
		if (!kitchenId) return;
		fetchFirstPage({
			kitchenId,
			hasRows: false,
			setTaskList,
			setLoading,
			setPendingNewer,
			setPendingIsGap,
			setPendingStartCursor,
		}).then(() => setLiveEnabled(true));
	}, [kitchenId]);

	useEffect(() => {
		if (!liveEnabled) return;
		if (!lastElement) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) return;
				fetchOlder({
					kitchenId,
					endCursor,
					hasMoreOlder,
					setTaskList,
					setLoadingOlder,
				});
			},
			{ threshold: 1 }
		);
		observer.observe(lastElement);
		return () => observer.disconnect();
	}, [liveEnabled, lastElement, kitchenId, endCursor, hasMoreOlder]);

	usePolling(
		() =>
			fetchNewer({
				kitchenId,
				startCursor,
				taskList,
				pendingNewer,
				pendingIsGap,
				setTaskList,
				setPendingNewer,
				setPendingIsGap,
				setPendingStartCursor,
			}),
		{ interval: POLL_INTERVAL_MS, enabled: liveEnabled && Boolean(startCursor), immediate: preserveState }
	);

	function navigateToTaskPage(taskId) {
		navigate(`/${machineID}/task/${taskId}`);
	}

	function setLastRowNode(node, isLastElement) {
		if (!isLastElement) return;
		if (!node) return;
		setLastElement((prev) => (prev === node ? prev : node));
	}

	function renderTaskList(tasks, listLoading) {
		if (tasks.length) {
			const sentinel = <div id={TASK_LIST_TOP_SENTINEL_ID} aria-hidden style={{ height: 1, width: "100%" }} />;
			if (isMobile) {
				return (
					<TaskListContainer>
						{sentinel}
						{tasks.map((task, index, array) => {
							const { _id, taskType, taskGroupId, status, actualEndTime, actualStartTime } = task;
							const taskStatus = getTaskStatusMeta(status);
							const isLastElement = index === array.length - 1;
							return (
								<TaskCard
									ref={(node) => setLastRowNode(node, isLastElement)}
									id={_id}
									key={_id}
									onClick={() => {
										setLastClickedElement({ id: _id });
										navigateToTaskPage(_id);
									}}>
									<Cell>
										<CellLabel>Task ID</CellLabel>
										<CellValue>{_id}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Group ID</CellLabel>
										<CellValue>{taskGroupId}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Type</CellLabel>
										<CellValue>{beautifyKey(taskType)}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Status</CellLabel>
										<StatusBadge background={taskStatus.backColor} color={taskStatus.textColor}>
											{taskStatus.label}
										</StatusBadge>
									</Cell>
									<Cell>
										<CellLabel>Start Time</CellLabel>
										<CellValue noEllipsis><Timestamp value={actualStartTime} format={LIST_DISPLAY_DATE_FORMAT} /></CellValue>
									</Cell>
									<Cell>
										<CellLabel>End Time</CellLabel>
										<CellValue noEllipsis><Timestamp value={actualEndTime} format={LIST_DISPLAY_DATE_FORMAT} /></CellValue>
									</Cell>
								</TaskCard>
							);
						})}
					</TaskListContainer>
				);
			}
			return (
				<>
					{sentinel}
					<Table tableLayout="fixed">
						<Trow head>
							<Thead>Task ID</Thead>
							<Thead>Task Type</Thead>
							<Thead>Task Group ID</Thead>
							<Thead>Task Status</Thead>
							<Thead>Start Time</Thead>
							<Thead>End Time</Thead>
						</Trow>
						<Tbody>
							{tasks.map((task, index, array) => {
								const { _id, taskType, taskGroupId, status, actualEndTime, actualStartTime } = task;
								return (
									<Trow
										id={_id}
										key={_id}
										innerRef={(node) => setLastRowNode(node, index === array.length - 1)}
										onClick={() => {
											setLastClickedElement({ id: _id });
											navigateToTaskPage(_id);
										}}>
										<Tcol borderRadius="10px 0 0 10px">{_id}</Tcol>
										<Tcol>{beautifyKey(taskType)}</Tcol>
										<Tcol>{taskGroupId}</Tcol>
										<Tcol>{TASK_STATUSES[status] || beautifyKey(status)}</Tcol>
										<Tcol><Timestamp value={actualStartTime} format={LIST_DISPLAY_DATE_FORMAT} /></Tcol>
										<Tcol borderRadius="0 10px 10px 0"><Timestamp value={actualEndTime} format={LIST_DISPLAY_DATE_FORMAT} /></Tcol>
									</Trow>
								);
							})}
						</Tbody>
					</Table>
				</>
			);
		} else if (!listLoading) {
			return (
				<EmptyState
					retry={() =>
						fetchFirstPage({
							kitchenId,
							hasRows: false,
							setTaskList,
							setLoading,
							setPendingNewer,
							setPendingIsGap,
							setPendingStartCursor,
						})
					}
				/>
			);
		}
	}

	return (
		<Page>
			<Header>Recent Tasks</Header>
			{(pendingNewer.length > 0 || pendingIsGap) && (
				<NewTasksChipWrap>
					<NewTasksChip
						type="button"
						disabled={chipBusy}
						aria-busy={chipBusy}
						onClick={() =>
							applyPendingNewer({
								pendingIsGap,
								pendingNewer,
								pendingStartCursor,
								kitchenId,
								hasRows: taskList.length > 0,
								setTaskList,
								setLoading,
								setPendingNewer,
								setPendingIsGap,
								setPendingStartCursor,
								setChipBusy,
							})
						}>
						{chipBusy ? <ChipSpinner aria-hidden /> : null}
						{pendingIsGap ? "New tasks" : `${pendingNewer.length} new ${pendingNewer.length === 1 ? "task" : "tasks"}`}
					</NewTasksChip>
				</NewTasksChipWrap>
			)}
			{renderTaskList(visibleTasks, loading)}
			{loading || loadingOlder ? (
				<CenteredContainer>
					<Loader />
				</CenteredContainer>
			) : null}
		</Page>
	);
};

const mapStateToProps = (state) => {
	const { task, landing } = state;
	return { ...task, ...landing };
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			...TaskActions,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskList);
